// src/components/notifications/RecipientSelector.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  ListItemAvatar,
  Avatar,
  Alert,
  Autocomplete,
  TextField,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Clear as ClearIcon,
  SelectAll as SelectAllIcon,
} from '@mui/icons-material';
import { FormSection } from '../../../components/common/form';

export default function RecipientSelector({
  selectedRecipients,
  onRecipientsChange,
  departments = [],
  allStaff = [],
  loading = false,
  error = null,
  validationError = null,
}) {
  const { t } = useTranslation();
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [departmentStaff, setDepartmentStaff] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [selectionMode, setSelectionMode] = useState('individual'); // 'individual' or 'department'

  // Filter staff by selected department
  useEffect(() => {
    if (selectedDepartment) {
      const filtered = allStaff.filter(staff => 
        staff.department_id === selectedDepartment &&
        (showInactive || staff.is_active)
      );
      setDepartmentStaff(filtered);
    } else {
      setDepartmentStaff(allStaff.filter(staff => showInactive || staff.is_active));
    }
  }, [selectedDepartment, allStaff, showInactive]);

  // Filter staff by search query
  const filteredStaff = departmentStaff.filter(staff =>
    staff.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (staff.department_name && staff.department_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Get available staff (not already selected)
  const availableStaff = filteredStaff.filter(staff =>
    !selectedRecipients.find(r => r.staff_member_id === staff.staff_member_id)
  );

  const handleAddRecipient = (staff) => {
    const newRecipients = [...selectedRecipients, staff];
    onRecipientsChange(newRecipients);
  };

  const handleRemoveRecipient = (staffId) => {
    const newRecipients = selectedRecipients.filter(r => r.staff_member_id !== staffId);
    onRecipientsChange(newRecipients);
  };

  const handleAddAllFromDepartment = () => {
    if (!selectedDepartment) return;

    const departmentMembers = allStaff.filter(staff => 
      staff.department_id === selectedDepartment &&
      (showInactive || staff.is_active) &&
      !selectedRecipients.find(r => r.staff_member_id === staff.staff_member_id)
    );

    const newRecipients = [...selectedRecipients, ...departmentMembers];
    onRecipientsChange(newRecipients);
  };

  const handleClearAll = () => {
    onRecipientsChange([]);
  };

  const handleDepartmentChange = (departmentId) => {
    setSelectedDepartment(departmentId);
    setSearchQuery('');
  };

  const getRecipientsByDepartment = () => {
    const grouped = selectedRecipients.reduce((acc, recipient) => {
      const deptName = recipient.department_name || t('notifications:recipient_selector.unknown_department');
      if (!acc[deptName]) {
        acc[deptName] = [];
      }
      acc[deptName].push(recipient);
      return acc;
    }, {});
    return grouped;
  };

  const groupedRecipients = getRecipientsByDepartment();
  const selectedDepartmentInfo = departments.find(d => d.department_id === selectedDepartment);
  const departmentStaffCount = selectedDepartment 
    ? allStaff.filter(s => s.department_id === selectedDepartment && (showInactive || s.is_active)).length 
    : 0;

  return (
    <FormSection
      title={t('notifications:recipient_selector.title', { count: selectedRecipients.length })}
      action={selectedRecipients.length > 0 && (
        <Button
          size="small"
          startIcon={<ClearIcon />}
          onClick={handleClearAll}
          color="error"
        >
          {t('notifications:recipient_selector.clear_all')}
        </Button>
      )}
    >
      <Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {validationError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        )}

        {/* Selection Mode Toggle */}
        <Box mb={3}>
          <FormControlLabel
            control={
              <Switch
                checked={selectionMode === 'department'}
                onChange={(e) => setSelectionMode(e.target.checked ? 'department' : 'individual')}
              />
            }
            label={t('notifications:recipient_selector.department_selection')}
          />
          <Typography variant="caption" color="text.secondary" display="block">
            {selectionMode === 'department' 
              ? t('notifications:recipient_selector.department_mode_help')
              : t('notifications:recipient_selector.individual_mode_help')
            }
          </Typography>
        </Box>

        {selectionMode === 'department' ? (
          /* Department Selection Mode */
          <Box mb={3}>
            <Typography variant="subtitle2" gutterBottom>
              {t('notifications:recipient_selector.select_departments')}
            </Typography>
            <Stack spacing={2}>
              {departments.map((dept) => {
                const deptStaff = allStaff.filter(s => 
                  s.department_id === dept.department_id && (showInactive || s.is_active)
                );
                const selectedFromDept = selectedRecipients.filter(r => 
                  r.department_id === dept.department_id
                ).length;
                const isFullySelected = selectedFromDept === deptStaff.length && deptStaff.length > 0;
                const isPartiallySelected = selectedFromDept > 0 && selectedFromDept < deptStaff.length;

                return (
                  <Card key={dept.department_id} variant="outlined">
                    <CardContent sx={{ py: 2 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={2}>
                          <BusinessIcon color="primary" />
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {dept.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('notifications:recipient_selector.staff_count', { count: deptStaff.length })}
                              {selectedFromDept > 0 && ` • ${t('notifications:recipient_selector.selected_count', { count: selectedFromDept })}`}
                            </Typography>
                          </Box>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          {isPartiallySelected && (
                            <Chip 
                              label={`${selectedFromDept}/${deptStaff.length}`} 
                              size="small" 
                              color="warning" 
                              variant="outlined" 
                            />
                          )}
                          {isFullySelected ? (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => {
                                const newRecipients = selectedRecipients.filter(r => 
                                  r.department_id !== dept.department_id
                                );
                                onRecipientsChange(newRecipients);
                              }}
                            >
                              {t('notifications:recipient_selector.remove_all')}
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              disabled={deptStaff.length === 0}
                              onClick={() => {
                                const newMembers = deptStaff.filter(staff =>
                                  !selectedRecipients.find(r => r.staff_member_id === staff.staff_member_id)
                                );
                                const newRecipients = [...selectedRecipients, ...newMembers];
                                onRecipientsChange(newRecipients);
                              }}
                            >
                              {isPartiallySelected ? t('notifications:recipient_selector.add_remaining') : t('notifications:recipient_selector.add_all')}
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          </Box>
        ) : (
          /* Individual Selection Mode */
          <>
            {/* Department Filter */}
            <Box mb={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="recipient-selector-department-filter-label">{t('notifications:recipient_selector.department_filter')}</InputLabel>
                <Select labelId="recipient-selector-department-filter-label"
                  value={selectedDepartment}
                  label={t('notifications:recipient_selector.department_filter')}
                  onChange={(e) => handleDepartmentChange(e.target.value)}
                  disabled={loading}
                >
                  <MenuItem value="">
                    <em>{t('notifications:recipient_selector.all_departments')}</em>
                  </MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.department_id} value={dept.department_id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <BusinessIcon fontSize="small" />
                        {dept.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedDepartment && (
                <Box mt={2} display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('notifications:recipient_selector.staff_in_department', { 
                      count: departmentStaffCount, 
                      department: selectedDepartmentInfo?.name 
                    })}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<SelectAllIcon />}
                    onClick={handleAddAllFromDepartment}
                    disabled={loading || departmentStaffCount === 0}
                  >
                    {t('notifications:recipient_selector.add_all_from_department')}
                  </Button>
                </Box>
              )}
            </Box>

            {/* Staff Search and Add */}
            <Box mb={3}>
              <Autocomplete
                options={availableStaff}
                getOptionLabel={(option) => `${option.name} (${option.department_name || t('notifications:recipient_selector.no_department')})`}
                onChange={(event, value) => {
                  if (value) {
                    handleAddRecipient(value);
                  }
                }}
                loading={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('notifications:recipient_selector.search_staff')}
                    placeholder={t('notifications:recipient_selector.search_placeholder')}
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                )}
                renderOption={({ key, ...props }, option) => (
                  <Box key={key} component="li" {...props}>
                    <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2">
                        {option.name}
                        {!option.is_active && (
                          <Chip label={t('notifications:recipient_selector.inactive')} size="small" color="default" sx={{ ml: 1 }} />
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.department_name || t('notifications:recipient_selector.no_department')}
                      </Typography>
                    </Box>
                  </Box>
                )}
                noOptionsText={
                  loading ? t('notifications:recipient_selector.loading') : 
                  availableStaff.length === 0 ? t('notifications:recipient_selector.no_available_staff') : 
                  t('notifications:recipient_selector.no_matching_staff')
                }
              />
            </Box>
          </>
        )}

        {/* Options */}
        <Box mb={3}>
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                size="small"
              />
            }
            label={t('notifications:recipient_selector.include_inactive')}
          />
        </Box>

        {/* Selected Recipients */}
        {selectedRecipients.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('notifications:recipient_selector.selected_recipients')}
            </Typography>
            
            {Object.entries(groupedRecipients).map(([deptName, recipients]) => (
              <Box key={deptName} mb={2}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <BusinessIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary" fontWeight="medium">
                    {deptName} ({recipients.length})
                  </Typography>
                </Box>
                
                <List dense>
                  {recipients.map((recipient) => (
                    <ListItem key={recipient.staff_member_id} divider>
                      <ListItemAvatar>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          <PersonIcon fontSize="small" />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            {recipient.name}
                            {!recipient.is_active && (
                              <Chip label={t('notifications:recipient_selector.inactive')} size="small" color="default" />
                            )}
                          </Box>
                        }
                        secondary={recipient.department_name}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title={t('notifications:recipient_selector.remove_recipient')}>
                          <IconButton
                            edge="end"
                            onClick={() => handleRemoveRecipient(recipient.staff_member_id)}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </Box>
        )}

        {/* Quick Stats */}
        {selectedRecipients.length > 0 && (
          <Box mt={2} p={2} bgcolor="action.hover" borderRadius={1}>
            <Typography variant="body2" color="text.secondary">
              {t('notifications:recipient_selector.summary', {
                count: selectedRecipients.length,
                departments: Object.keys(groupedRecipients).length
              })}
            </Typography>
          </Box>
        )}
      </Box>
    </FormSection>
  );
}
